import { Spinner, SpinnerSize } from "@fluentui/react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { FocusTrap } from "focus-trap-react";
import { CloseIcon } from "../../assets/closeIcon";
import styles from "./PowerBIOverlay.module.css";

interface PowerBIEmbedConfig {
    reportId: string;
    embedUrl: string;
    accessToken: string;
}

interface PowerBIOverlayProps {
    isOpen: boolean;
    iframeLoaded: boolean;
    error: string | null;
    embedConfig: PowerBIEmbedConfig | null;
    onClose: () => void;
    onEmbedLoaded: () => void;
    onEmbedError: (event: any) => void;
    onGetEmbeddedComponent: (embeddedReport: any) => void;
    onFocusTrapChange: (active: boolean) => void;
}

export const PowerBIOverlay = ({
    isOpen,
    iframeLoaded,
    error,
    embedConfig,
    onClose,
    onEmbedLoaded,
    onEmbedError,
    onGetEmbeddedComponent,
    onFocusTrapChange,
}: PowerBIOverlayProps) => {
    return (
        <FocusTrap
            active={isOpen}
            focusTrapOptions={{
                initialFocus: () => document.getElementById("return-to-priya-from-powerbi"),
                fallbackFocus: () => document.getElementById("powerbi-dialog") || document.body,
                returnFocusOnDeactivate: false,
                escapeDeactivates: false,
                onActivate: () => onFocusTrapChange(true),
                onDeactivate: () => onFocusTrapChange(false),
            }}
        >
            <div
                id="powerbi-dialog"
                className={`${styles.powerBIOverlay} ${!isOpen ? styles.powerBIOverlayHidden : ""}`}
                role="dialog"
                aria-modal={isOpen}
                aria-label="Power BI"
                aria-hidden={!isOpen}
                tabIndex={-1}
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        event.preventDefault();
                        onClose();
                    }
                }}
            >
                <button
                    type="button"
                    id="return-to-priya-from-powerbi"
                    aria-label="Return to Priya"
                    title="Return to Priya"
                    className={styles.powerBIOverlayReturnButton}
                    onClick={onClose}
                >
                    <CloseIcon fill="#fff" role="presentation" />
                    <span>Return to Priya</span>
                </button>
                <div
                    className={`${styles.powerBIOverlayLoading} ${iframeLoaded && !error ? styles.powerBIOverlayLoadingDone : ""}`}
                    aria-hidden={iframeLoaded && !error}
                >
                    {error ? <span role="alert">{error}</span> : <Spinner size={SpinnerSize.large} label="Loading..." />}
                </div>
                {embedConfig && (
                    <PowerBIEmbed
                        // Forces a real remount (fresh powerbi.embed() call) on every
                        // program switch — accessToken changes each fetch, so this is
                        // never stale even though the reportId/embedUrl stay the same.
                        key={embedConfig.accessToken}
                        embedConfig={{
                            type: "report",
                            id: embedConfig.reportId,
                            embedUrl: embedConfig.embedUrl,
                            accessToken: embedConfig.accessToken,
                            tokenType: models.TokenType.Embed,
                        }}
                        eventHandlers={
                            new Map([
                                ["loaded", onEmbedLoaded],
                                ["error", onEmbedError],
                            ])
                        }
                        cssClassName={styles.powerBIOverlayIframe}
                        getEmbeddedComponent={onGetEmbeddedComponent}
                    />
                )}
            </div>
        </FocusTrap>
    );
};
